/**
 * Hero.jsx
 * ---------------------------------------------------------------------------
 * Section 1. Left: headline + subhead + CTAs + small stat row.
 * Right: an abstract "raw inquiries → structured intake → qualified flow"
 * SVG visualization using subtle animated path flows against a faint grid.
 */

import React from "react";
import Button from "../ui/Button.jsx";
import { tokens } from "../../lib/tokens.js";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  return (
    <section
      style={{
        padding: "160px 32px 140px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1.15fr 1fr",
          gap: 80,
          alignItems: "center",
        }}
      >
        {/* Copy column */}
        <div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: tokens.inkSoft,
              marginBottom: 32,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <span style={{ width: 28, height: 1, background: tokens.ink }} />
            Legal Intelligence · Est. Mandate
          </div>

          <h1
            style={{
              fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "clamp(44px, 6.4vw, 80px)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              color: tokens.ink,
              margin: 0,
            }}
          >
            Signal Over Noise
            <br />
            <em style={{ fontStyle: "italic", color: tokens.accent }}>
              in Legal Work.
            </em>
          </h1>

          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 18,
              lineHeight: 1.6,
              color: tokens.inkSoft,
              maxWidth: 520,
              marginTop: 36,
              fontWeight: 400,
            }}
          >
            Structured legal demand. Qualified case flow. Economic intelligence
            for modern firms.
          </p>

          <div style={{ display: "flex", gap: 12, marginTop: 48 }}>
            <Button onClick={() => scrollTo("individuals")}>
              Submit a Case
            </Button>
            <Button variant="ghost" onClick={() => scrollTo("firms")}>
              For Law Firms
            </Button>
          </div>

          {/* Stat row */}
          <div
            style={{
              marginTop: 72,
              paddingTop: 28,
              borderTop: `1px solid ${tokens.divider}`,
              display: "flex",
              gap: 48,
            }}
          >
            {[
              ["Structured", "Intake"],
              ["Qualified", "Routing"],
              ["Economic", "Analytics"],
            ].map(([a, b]) => (
              <div key={a}>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    color: tokens.ink,
                    fontWeight: 500,
                  }}
                >
                  {a}
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 12,
                    color: tokens.inkSoft,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginTop: 4,
                  }}
                >
                  {b}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Visualization column */}
        <HeroVisualization />
      </div>
    </section>
  );
}

function HeroVisualization() {
  return (
    <div style={{ position: "relative", height: 520 }}>
      <svg
        viewBox="0 0 500 560"
        style={{ width: "100%", height: "100%" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="flowFade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={tokens.accent} stopOpacity="0" />
            <stop offset="0.5" stopColor={tokens.accent} stopOpacity="0.55" />
            <stop offset="1" stopColor={tokens.ink} stopOpacity="0.9" />
          </linearGradient>
        </defs>

        {/* Faint grid */}
        {Array.from({ length: 11 }).map((_, i) => (
          <line
            key={`v${i}`}
            x1={i * 50}
            y1="0"
            x2={i * 50}
            y2="560"
            stroke={tokens.divider}
            strokeWidth="0.5"
          />
        ))}
        {Array.from({ length: 12 }).map((_, i) => (
          <line
            key={`h${i}`}
            x1="0"
            y1={i * 50}
            x2="500"
            y2={i * 50}
            stroke={tokens.divider}
            strokeWidth="0.5"
          />
        ))}

        {/* Converging flow lines */}
        {Array.from({ length: 14 }).map((_, i) => {
          const startX = 30 + i * 30;
          return (
            <path
              key={i}
              d={`M ${startX} 40 Q ${startX} 240 250 340 T 250 520`}
              fill="none"
              stroke="url(#flowFade)"
              strokeWidth="0.8"
              opacity={0.4 + (i % 3) * 0.2}
            >
              <animate
                attributeName="opacity"
                values="0.2;0.7;0.2"
                dur={`${4 + (i % 3)}s`}
                repeatCount="indefinite"
              />
            </path>
          );
        })}

        {/* Top nodes */}
        {Array.from({ length: 14 }).map((_, i) => (
          <circle
            key={`n${i}`}
            cx={30 + i * 30}
            cy={40}
            r="2"
            fill={tokens.ink}
            opacity="0.5"
          />
        ))}

        {/* Convergence point */}
        <circle
          cx="250"
          cy="340"
          r="6"
          fill={tokens.bg}
          stroke={tokens.ink}
          strokeWidth="1.5"
        />
        <circle cx="250" cy="340" r="2" fill={tokens.accent} />

        {/* Output line */}
        <line
          x1="250"
          y1="346"
          x2="250"
          y2="520"
          stroke={tokens.ink}
          strokeWidth="1.2"
        />

        {/* Output block */}
        <rect x="230" y="514" width="40" height="12" fill={tokens.ink} />

        {/* Labels */}
        <text
          x="20"
          y="22"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill={tokens.inkSoft}
        >
          RAW INQUIRIES
        </text>
        <text
          x="270"
          y="344"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill={tokens.ink}
        >
          STRUCTURED
        </text>
        <text
          x="270"
          y="356"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill={tokens.ink}
        >
          INTAKE
        </text>
        <text
          x="20"
          y="540"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          letterSpacing="2"
          fill={tokens.inkSoft}
        >
          QUALIFIED CASE FLOW →
        </text>
      </svg>
    </div>
  );
}
